import type { IRadioButton } from "@Layout/RadioButton/RadioButton";
import RadioButton from "@Layout/RadioButton/RadioButton";
import * as stylex from "@stylexjs/stylex";

import type { IComponentAjax, IComponentAjaxState } from "../../Components/src/ComponentAjax";
import { ComponentAjax } from "../../Components/src/ComponentAjax";
import type { IInput } from "../../Components/src/Input";
import { Loading } from "../../Components/src/Loading";

export interface IRadioButtonList extends IComponentAjax<string>, IInput {
    groupName: string;
    options: IRadioButton[];
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    valueParameter?: string;
    variant?: "horizontal";
    dataTestId?: string;
}

interface IRadioButtonListState extends IComponentAjaxState {
    checkedValue?: string;
}

export class RadioButtonList extends ComponentAjax<string, IRadioButtonList, IRadioButtonListState> {
    public static defaultProps: IRadioButtonList = {
        ...ComponentAjax.defaultProps,
        groupName: "",
        options: [],
    };

    private lastValue: string = "";

    constructor(props: IRadioButtonList) {
        super(props);

        this.state = {
            checkedValue: this.props.initialData,
            isLoading: false,
        };
    }

    public render() {
        if (!this.props.options) {
            return null;
        }

        const options = this.props.options.map((option, i) => {
            const isChecked = this.state.checkedValue
                ? this.state.checkedValue.toLowerCase() === `${option.value}`.toLowerCase()
                : false;
            const clientId = this.props.clientId
                ? `${this.props.clientId}-${i}`
                : `${this.props.groupName}-${i}`;
            return (
                <RadioButton
                    key={i}
                    {...option}
                    id={clientId}
                    name={this.props.groupName}
                    checked={isChecked}
                    onChange={this.onRadioChange}
                />
            );
        });

        return (
            <div
                {...stylex.props(styles.wrapper, this.props.variant && styles[this.props.variant])}
                data-testid={this.props.dataTestId}
            >
                {options}
                {this.props.ajaxAction && this.state.isLoading && <Loading isModal />}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: string): void {
        if (typeof response === "string") {
            this.setState({ isLoading: false, checkedValue: response });
        } else {
            this.setState({ isLoading: false, checkedValue: this.lastValue });
        }
    }

    protected ajaxFail(): boolean {
        this.setState(() => ({ isLoading: false }));
        return true;
    }

    private onRadioChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;

        if (!this.props.ajaxAction) {
            this.setState({ checkedValue: value });
            if (this.props.onChange) {
                this.props.onChange(event);
            }

            return;
        }

        this.lastValue = value;
        this.setState({ isLoading: true });

        const param: { [key: string]: string } = {};

        if (this.props.valueParameter) {
            param[this.props.valueParameter] = value;
        }

        this.ajaxRequest(param);
    };
}

const styles = stylex.create({
    wrapper: { display: "flex", gap: "1.5em", margin: "0.3em 0" },
    horizontal: {
        display: "grid",
        gridTemplateColumns: "1fr",
    },
});
